import { useEffect, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { Cog6ToothIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { AnalyticsEvents } from '@pantry/analytics';
import { Button } from '@pantry/ui';
import { useResetTour } from '@pantry/api-client';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useTourContext } from '../../components/onboarding/useTourContext';
import { ProfileSection } from '../../components/settings/profile-section';
import { PasswordSection } from '../../components/settings/password-section';
import { EmailSection } from '../../components/settings/email-section';
import { LinkedAccountsSection } from '../../components/settings/linked-accounts-section';
import { PreferencesSection } from '../../components/settings/preferences-section';
import { NotificationsSection } from '../../components/settings/notifications-section';
import { ToursSection } from '../../components/settings/tours-section';
import { AccountSection } from '../../components/settings/account-section';

export const Route = createFileRoute('/_app/settings')({
  component: SettingsPage,
});

const SETTINGS_TOUR_ID = 'settings-basics' as const;

function SettingsPage() {
  const { t } = useLingui();
  const { track } = useAnalytics();
  const { startTour } = useTourContext();
  const resetTour = useResetTour();
  const [isStartingTour, setIsStartingTour] = useState(false);

  // Track OAuth completion when returning from account linking
  // Read from window.location to avoid making search params required for all Links
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const linked = params.get('linked');
    if (linked) {
      track(AnalyticsEvents.AUTH_OAUTH_COMPLETED, {
        provider: linked,
        flow: 'link_account',
      });
      track(AnalyticsEvents.ACCOUNT_LINKED, { provider: linked });
      // Clean up the URL
      window.history.replaceState({}, '', '/settings');
    }
  }, [track]);

  const handleStartTour = async () => {
    if (isStartingTour) return;

    setIsStartingTour(true);
    try {
      await resetTour.mutateAsync({ tourId: SETTINGS_TOUR_ID, deviceType: 'web' });
      await startTour(SETTINGS_TOUR_ID);
    } catch (tourError) {
      console.error('Failed to start settings tour:', tourError);
    } finally {
      setIsStartingTour(false);
    }
  };

  return (
    <div className="space-y-6" data-tour="settings-page-intro">
      {/* Page header */}
      <div
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        data-tour="settings-header"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
            <Cog6ToothIcon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              <Trans context="page_title">Settings</Trans>
            </h1>
            <p className="text-muted-foreground">
              <Trans>Manage your profile and account preferences.</Trans>
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void handleStartTour()}
          data-tour="settings-help"
          aria-label={t`Start settings tour`}
          title={t`Settings help`}
          aria-busy={isStartingTour}
          disabled={isStartingTour}
          className="gap-1.5 rounded-xl self-start sm:self-auto"
        >
          <QuestionMarkCircleIcon className="h-5 w-5" />
          <span>
            <Trans context="action_button">Tour</Trans>
          </span>
        </Button>
      </div>

      <div className="space-y-6" data-tour="settings-sections">
        {/* Profile Section */}
        <ProfileSection />

        {/* Security Section */}
        <PasswordSection />

        {/* Email Section */}
        <EmailSection />

        {/* Linked Accounts Section */}
        <LinkedAccountsSection />

        {/* Preferences Section */}
        <PreferencesSection />

        {/* Notifications Section */}
        <NotificationsSection />

        {/* Tours Section */}
        <ToursSection />

        {/* Account Deletion Section */}
        <AccountSection />
      </div>
    </div>
  );
}
